import { CustomerUserInfoDataFragment } from 'graph/generated/payments/graphql-types'

/**
 * Picks which of the signed-in person's memberships in this portal is active.
 *
 * The CustomerId cookie can hold a customer id that isn't among the person's
 * memberships returned by `GetUserInfo`: the `?cid=` of an emailed invoice
 * link (e.g. an archived duplicate customer) or a stale team selection, so
 * it is only one candidate. Precedence:
 * - on an invoice page (`preferInvoice`): the invoice's customer — the
 *   invoice the person is trying to pay — then the cookie's customer;
 * - elsewhere: the cookie's customer — an explicit team selection — then
 *   the invoice's customer;
 * - otherwise the first membership.
 */
export const selectActiveUser = (
  users: CustomerUserInfoDataFragment[] | null | undefined,
  {
    cookieCustomerId,
    invoiceCustomerId,
    preferInvoice
  }: {
    cookieCustomerId?: string
    invoiceCustomerId?: string
    preferInvoice?: boolean
  }
): CustomerUserInfoDataFragment | undefined => {
  if (!users?.length) return undefined

  const byCustomer = (customerId?: string) =>
    customerId ? users.find(u => u.customer?.id === customerId) : undefined

  const [first, second] = preferInvoice
    ? [invoiceCustomerId, cookieCustomerId]
    : [cookieCustomerId, invoiceCustomerId]

  return byCustomer(first) ?? byCustomer(second) ?? users[0]
}
